import React, { useState, useRef, useEffect, useCallback } from "react";
import { Search, Plus, Phone, Video, MoreHorizontal, ArrowLeft, Trash2, Info, Users, User } from "lucide-react";
import { Constants } from "../../../utils/constants";
import { messagingService } from "../../../services/host-apiServices/messagingService";
import { apiV2Service, organizationApiService } from "../../../services/apiV2Service";
import { getUser } from "../../../services/localService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../../shadcn/ui/popover";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import MessageInput from "./components/MessageInput";
import { showApiMessage } from "../../../utils/messageHandler";
import { getCurrentUserOrganizationId } from "../host-clients/utils/hostClientsUtils";
import * as localService from "../../../services/localService";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import toastUtils from "../../../utils/toast";
import HUpgradeButton from "../../../hcomponents/HUpgradeButton";
import HReusableAlertModal from "../../../hcomponents/HReusableAlertModal";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import logger from "../../../utils/logger";


// Check if a URL is a generic/placeholder avatar (not a real user photo)
function isPlaceholderAvatar(url) {
  if (!url) return true;
  return url.includes("placehold.co") || url.includes("ui-avatars.com") || url === "";
}

// Generate a ui-avatars URL with proper sizing and random colors
function getInitialsAvatar(name, size = 48) {
  const displayName = name || "U";
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&color=fff&size=${size}`;
}

// Best avatar URL from a participant — skips placeholders, falls back to colored initials
function participantAvatarUrl(p, size = 48) {
  if (!p) return getInitialsAvatar("U", size);
  const url = p.avatarUrl || p.avatar || p.imageUrl;
  if (url && !isPlaceholderAvatar(url)) return url;
  const name = p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.name || p.firstName || p.username || "U";
  return getInitialsAvatar(name, size);
}

// Get the other participant in a single chat (excludes current user)
function getOtherParticipant(participants) {
  const currentUser = getUser();
  const currentId = currentUser?.id || currentUser?.userId;
  return participants?.find(p => {
    const pId = p.id || p._id || p.userId;
    return pId !== currentId;
  });
}

// Helper to format message dates WhatsApp-style
function formatMessageDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const _diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin === 1) return "a minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffHour === 1) return "an hour ago";
  if (diffHour < 24 && date.toDateString() === now.toDateString()) {
    // Today, show time
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  // Check for yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  // This year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  }
  // Previous years
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const HostMessages = () => {
  const history = useHistory();
  const featureFlags = useSelector(state => state.subscription?.featureFlags || {});
  const canMessageTeam = FeatureAccess.canUpgradePlan(getUser()) ? !!featureFlags?.TEAM_MESSAGING : true;
  const canMessageCustomers = FeatureAccess.canUpgradePlan(getUser()) ? !!featureFlags?.CUSTOMER_MESSAGING : true;

  const goToUpgrade = useCallback(() => {
    history.push("/host/pricing?view=packages");
  }, [history]);

  const showUpgradePrompt = useCallback(
    target => {
      const description = target === "team" ? "Upgrade to message your team." : "Upgrade to message your customers.";
      toastUtils.info(description, {
        action: {
          label: "View plans",
          onClick: goToUpgrade,
        },
      });
    },
    [goToUpgrade]
  );

  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const currentUser = localService.getUser();
  const userRoleCode = currentUser?.organizationRole?.code || currentUser?.role;
  const isIndividualCustomer = userRoleCode === "ROLE_HOST_CUSTOMER_INDIVIDUAL";
  const hasParentOrg = FeatureAccess.hasParentStaffTabAccess(currentUser);

  const showStaffTab = FeatureAccess.hasStaffTabAccess(currentUser);
  const showClientsTab = FeatureAccess.hasClientsTabAccess(currentUser) && !isIndividualCustomer;

  // Define available tabs based on permissions
  const availableTabs = [];
  if (showStaffTab) availableTabs.push("Staff");
  if (showClientsTab) availableTabs.push("Clients");

  const [activeTab, setActiveTab] = useState(availableTabs[0] || "Staff");
  const [showChatArea, setShowChatArea] = useState(false);

  // Organization selection for client search
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newConversationName, setNewConversationName] = useState("");
  const [newConversationUserIds, setNewConversationUserIds] = useState([]);
  const [initialMessage, setInitialMessage] = useState("");
  const [_availableUsers, _setAvailableUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const [searchMode, setSearchMode] = useState("staff");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");

  // User search results excluding current user
  const filteredSearchResults = userSearchResults.filter(user => {
    const uid = user.id || user._id;
    return uid !== currentUser?.id && uid !== currentUser?.userId;
  });

  // User profile modal state
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Contact selection modal state
  const [showContactSelection, setShowContactSelection] = useState(false);
  const [contactAction, setContactAction] = useState(null); // 'call' or 'email'
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);

  // Message options popover state
  const [openMessageOptions, setOpenMessageOptions] = useState(null);

  // Chat type selection state
  const [chatType, setChatType] = useState("single"); // "single" or "group"

  // Chat info panel state
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [chatMembers, setChatMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Cache for selected users to maintain their data
  const [selectedUsersCache, setSelectedUsersCache] = useState({});

  // Delete confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);

  // Update active tab if it becomes unavailable
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs, activeTab]);

  const isStaffTab = activeTab === "Staff";
  const isClientTab = activeTab === "Clients";
  const staffFeatureDisabled = isStaffTab && !canMessageTeam;
  const clientFeatureDisabled = isClientTab && !canMessageCustomers;
  const currentTabFeatureDisabled = staffFeatureDisabled || clientFeatureDisabled;
  const currentTabUpgradeCopy = staffFeatureDisabled
    ? "Team messaging is available on higher plans."
    : clientFeatureDisabled
      ? "Customer messaging is available on higher plans."
      : "";

  const handleTabChange = useCallback(
    tab => {
      if (tab === "Staff" && !canMessageTeam) {
        showUpgradePrompt("team");
      }
      if (tab === "Clients" && !canMessageCustomers) {
        showUpgradePrompt("customers");
      }
      setActiveTab(tab);
    },
    [canMessageCustomers, canMessageTeam, showUpgradePrompt]
  );

  const handleOpenCreateModal = useCallback(() => {
    if (isStaffTab && !canMessageTeam) {
      showUpgradePrompt("team");
      return;
    }
    if (isClientTab && !canMessageCustomers) {
      showUpgradePrompt("customers");
      return;
    }
    setShowCreateModal(true);
  }, [canMessageCustomers, canMessageTeam, isClientTab, isStaffTab, showUpgradePrompt]);

  // Handle message deletion
  const handleDeleteMessage = async messageId => {
    try {
      const response = await messagingService.deleteMessage(messageId);
      showApiMessage(response, "delete message");
      // Refresh messages after delete
      const res = await messagingService.getMessages(activeContact?.id);
      const messagesData = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setMessages(messagesData);
      setOpenMessageOptions(null); // Close popover
    } catch (err) {
      showApiMessage(err, "delete message");
      setError("Failed to delete message");
    }
  };

  // Handle conversation deletion
  const handleDeleteConversation = (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering contact selection
    setConversationToDelete(conversationId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      const response = await messagingService.deleteConversation(conversationToDelete);
      showApiMessage(response, "delete conversation");

      // Remove from contacts list
      setContacts(prev => prev.filter(contact => contact.id !== conversationToDelete));

      // If this was the active conversation, clear it
      if (activeContact?.id === conversationToDelete) {
        setActiveContact(null);
        setMessages([]);
        setShowChatArea(false);
      }

      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    } catch (err) {
      logger.error("Failed to delete conversation:", err);
      showApiMessage(err, "delete conversation");
      setError("Failed to delete conversation");
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    }
  };

  // Handle chat type change
  const handleChatTypeChange = type => {
    setChatType(type);
    // Reset form when changing chat type
    setNewConversationName("");
    setNewConversationUserIds([]);
    setError(null);
  };

  // Handle call/email action
  const handleContactAction = async action => {
    const currentUser = getUser();

    // Get unique user IDs from messages, excluding current user
    const uniqueUserIds = [...new Set(messages.map(msg => msg.user?.id).filter(id => id && id !== currentUser?.id))];

    if (uniqueUserIds.length === 0) {
      toastUtils.info("No other participants in this chat");
      return;
    }

    if (uniqueUserIds.length === 1) {
      // Single participant - fetch details and confirm
      setContactAction(action);
      setLoadingUserDetails(true);
      try {
        const response = await apiV2Service.getUserById(uniqueUserIds[0]);
        const userData = response.data?.data || response.data;
        setSelectedUser(userData);
        setShowContactSelection(true);
      } catch (error) {
        logger.error("Failed to fetch user details:", error);
        toastUtils.error("Failed to load contact details");
      } finally {
        setLoadingUserDetails(false);
      }
    } else {
      // Multiple participants - show selection list
      setContactAction(action);
      setShowContactSelection(true);
    }
  };

  // Execute contact action
  const executeContactAction = user => {
    if (user.phoneNumber || user.phone) {
      window.location.href = `tel:${user.phoneNumber || user.phone}`;
    } else {
      toastUtils.info("No phone number available for this user");
    }
    setShowContactSelection(false);
    setSelectedUser(null);
  };

  // Reset modal state
  const resetCreateModal = () => {
    setChatType("single");
    setSearchMode("staff");
    setNewConversationName("");
    setNewConversationUserIds([]);
    setInitialMessage("");
    setSelectedUsersCache({});
    setError(null);
    setUserSearchTerm("");
    setUserSearchResults([]);
    setSelectedOrgId(null); // Reset org selection
  };

  // Handle chat info panel
  const handleShowChatInfo = async () => {
    setShowChatInfo(true);
    setLoadingMembers(true);

    try {
      // Fetch chat members - you might need to implement this API call
      // For now, we'll use the contact data to determine if it's a group or single chat
      if (activeContact) {
        // If the contact has multiple participants or is explicitly marked as group
        const isGroup = activeContact.isGroup || (activeContact.participants && activeContact.participants.length > 1);

        if (isGroup) {
          // For group chats, fetch all participants
          setChatMembers(activeContact.participants || []);
        } else {
          // For single chats, show the other participant
          const currentUser = getUser();
          const otherParticipant = activeContact.participants?.find(p => p.id !== currentUser?.id) || activeContact;
          setChatMembers([otherParticipant]);
        }
      }
    } catch (error) {
      logger.error("Error fetching chat members:", error);
      setError("Failed to load chat members");
    } finally {
      setLoadingMembers(false);
    }
  };

  // Determine if current chat is a group chat
  const isGroupChat = () => {
    if (!activeContact) return false;
    return activeContact.isGroup || (activeContact.participants && activeContact.participants.length > 2);
  };

  // Helper function to generate conversation name for single chats
  const generateSingleChatName = (userIds, userCache) => {
    const currentUser = getUser();
    const currentName =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : currentUser?.firstName || currentUser?.name || currentUser?.username || "You";

    // Get the other user from cache
    const otherUserId = userIds.find(id => id !== currentUser?.id);
    const otherUser = otherUserId ? userCache[otherUserId] : null;
    const otherName =
      otherUser?.firstName && otherUser?.lastName
        ? `${otherUser.firstName} ${otherUser.lastName}`
        : otherUser?.firstName || otherUser?.name || otherUser?.username || "User";

    // Return formatted name: "currentUser & otherUser"
    return `${currentName} & ${otherName}`;
  };

  // Helper function to get display name for single chat from participants
  const getSingleChatDisplayName = participants => {
    const currentUser = getUser();
    const currentUserId = currentUser?.id || currentUser?.userId || currentUser?._id;

    const currentParticipant = participants?.find(p => {
      const pId = p.id || p._id || p.userId;
      return pId === currentUserId;
    });
    const otherParticipant = participants?.find(p => {
      const pId = p.id || p._id || p.userId;
      return pId !== currentUserId;
    });

    const currentName =
      currentParticipant?.firstName && currentParticipant?.lastName
        ? `${currentParticipant.firstName} ${currentParticipant.lastName}`
        : currentParticipant?.name || currentParticipant?.firstName || (currentUser?.firstName && currentUser?.lastName)
          ? `${currentUser.firstName} ${currentUser.lastName}`
          : currentUser?.name || currentUser?.username || "You";

    const otherName =
      otherParticipant?.firstName && otherParticipant?.lastName
        ? `${otherParticipant.firstName} ${otherParticipant.lastName}`
        : otherParticipant?.name || otherParticipant?.firstName || otherParticipant?.username || "User";

    return `${currentName} & ${otherName}`;
  };

  // Handle sending message with automatic conversation creation
  const handleSendMessageWithCreation = async messageText => {
    logger.log("📤 handleSendMessageWithCreation called", {
      messageText,
      activeContact,
      newConversationUserIds,
      chatType,
      selectedUsersCache,
    });

    const targetIsClient = activeContact ? isClientConversation(activeContact) : !!selectedOrgId;
    if (targetIsClient && !canMessageCustomers) {
      showUpgradePrompt("customers");
      return;
    }
    if (!targetIsClient && !canMessageTeam) {
      showUpgradePrompt("team");
      return;
    }

    if (!activeContact && newConversationUserIds.length === 0) {
      setError("Please select a contact to send a message");
      logger.log("❌ Cannot send: No contact selected and no user IDs");
      return;
    }

    setSendingMessage(true);
    try {
      let conversationId = activeContact?.id || activeContact?._id;

      // If no active conversation, create one with the initial message
      if (!conversationId && newConversationUserIds.length > 0) {
        logger.log("🆕 Creating conversation with initial message...", {
          chatType,
          newConversationUserIds,
          selectedUsersCache,
          newConversationName,
        });

        // Generate conversation name based on chat type
        let conversationName =
          chatType === "single"
            ? generateSingleChatName(newConversationUserIds, selectedUsersCache)
            : newConversationName.trim() || "New Group Chat";

        // If this is a client conversation (selectedOrgId exists), append |client marker
        if (selectedOrgId) {
          conversationName = `${conversationName}|client`;
        }

        // Create conversation WITH the initial message (new API requirement)
        const conversationResponse = await messagingService.createConversation({
          name: conversationName,
          userIds: newConversationUserIds,
          message: {
            text: messageText,
          },
        });

        const newConversation = conversationResponse.data?.conversation || conversationResponse.data?.data;
        if (!newConversation) {
          throw new Error("Failed to create conversation");
        }

        // Add to contacts and set as active
        setContacts(prev => [newConversation, ...prev]);
        setActiveContact(newConversation);
        conversationId = newConversation.id || newConversation._id;

        // Clear the creation form
        setNewConversationName("");
        setNewConversationUserIds([]);
        setSelectedUsersCache({});
        setShowCreateModal(false);

        // Refresh messages after creating conversation (the initial message is already there)
        const res = await messagingService.getMessages(conversationId);
        const messagesData = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setMessages(messagesData);
      } else {
        // Existing conversation - just send the message
        await messagingService.sendMessage({
          conversationId: conversationId,
          text: messageText,
        });

        // Refresh messages after sending
        const res = await messagingService.getMessages(conversationId);
        const messagesData = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setMessages(messagesData);
      }
      // No toast for sending messages - it's too frequent
    } catch (err) {
      logger.error("Failed to send message:", err);
      showApiMessage(err, "send message");
      setError("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Fetch client organizations when create modal opens
  useEffect(() => {
    if (!showCreateModal) return;

    const fetchClientOrgs = async () => {
      try {
        setLoadingOrgs(true);
        const currentUser = localService.getUser();
        const parentOrganizationId = currentUser?.organizationId || currentUser?.organization?.id;

        if (!parentOrganizationId) {
          logger.warn("No parent organization ID found");
          setLoadingOrgs(false);
          return;
        }

        const response = await organizationApiService.getOrganizations({
          parentId: parentOrganizationId,
          page: 1,
          limit: 100, // Get all client orgs
        });

        const { data } = response.data;
        const organizations = Array.isArray(data) ? data : [];
        setClientOrgs(organizations);
      } catch (error) {
        logger.error("Failed to load client organizations:", error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    if (clientOrgs.length === 0) {
      fetchClientOrgs();
    }
  }, [showCreateModal]);

  // User search effect - searches by searchMode: staff (own org), parent_staff (parent org), or clients (selected org)
  useEffect(() => {
    if (!userSearchTerm.trim() || userSearchTerm.trim().length < 1) {
      setUserSearchResults([]);
      setUserSearchError("");
      return;
    }
    setUserSearchLoading(true);
    setUserSearchError("");

    const currentUserData = localService.getUser();
    let organizationId;
    let searchParams;

    if (searchMode === "clients") {
      if (!selectedOrgId) {
        setUserSearchError("Organization ID not found. Please refresh and try again.");
        setUserSearchLoading(false);
        return;
      }
      searchParams = { q: userSearchTerm.trim(), limit: 10, mode: "CLIENTS", status: "ACTIVE" };
    } else {
      organizationId =
        searchMode === "parent_staff" ? currentUserData?.organization?.parentId : getCurrentUserOrganizationId();
      if (!organizationId) {
        setUserSearchError("Organization ID not found. Please refresh and try again.");
        setUserSearchLoading(false);
        return;
      }
      searchParams = { q: userSearchTerm.trim(), limit: 10, organizationId, status: "ACTIVE" };
    }

    apiV2Service
      .searchUsers(searchParams)
      .then(res => {
        let users = [];
        if (Array.isArray(res?.data?.data)) {
          users = res.data.data;
        } else if (Array.isArray(res?.data)) {
          users = res.data;
        }
        setUserSearchResults(users);
      })
      .catch(err => {
        logger.error("User search error:", err);
        setUserSearchError("Failed to search users");
        setUserSearchResults([]);
      })
      .finally(() => setUserSearchLoading(false));
  }, [userSearchTerm, selectedOrgId, searchMode]);

  // Fetch conversations on mount
  useEffect(() => {
    setLoadingConversations(true);
    messagingService
      .listConversations()
      .then(res => {
        // Handle conversations API structure - /conversations returns conversation objects
        const conversations = res.data?.data || [];

        // Sort conversations by latest activity (updatedOn or lastMessage timestamp)
        const sortedConversations = conversations.sort((a, b) => {
          const aTime = new Date(a.updatedOn || a.lastMessage?.createdOn || a.createdOn);
          const bTime = new Date(b.updatedOn || b.lastMessage?.createdOn || b.createdOn);
          return bTime - aTime;
        });

        setContacts(sortedConversations);
        // Don't auto-select first conversation - let user choose
        // if (conversations.length > 0) {
        //   setActiveContact(conversations[0]);
        // }
      })
      .catch(err => {
        logger.error("Failed to load conversations:", err);
        setError("Failed to load conversations");
      })
      .finally(() => setLoadingConversations(false));
  }, []);

  // Fetch messages when activeContact changes
  useEffect(() => {
    if (!activeContact) return;
    setLoadingMessages(true);
    messagingService
      .getMessages(activeContact.id || activeContact._id)
      .then(res => {
        // Support both wrapped ({ data: [...] }) and bare ([...]) response formats
        const messagesData = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setMessages(messagesData);

        // Mark messages as read when conversation is opened with unread messages
        // Use conversation-level unreadCount (not message-level isRead) because
        // isRead is a global "anyone seen this" flag, not per-user read status
        const conversationUnread = activeContact.unreadCount || 0;
        if (conversationUnread > 0 && messagesData.length > 0) {
          const allMessageIds = messagesData.map(msg => msg.id || msg._id).filter(Boolean);

          if (allMessageIds.length > 0) {
            messagingService
              .markMessagesAsRead({
                conversationId: activeContact.id || activeContact._id,
                messageIds: allMessageIds,
              })
              .then(() => {
                // Update unreadCount locally
                setContacts(prev =>
                  prev.map(c =>
                    (c.id || c._id) === (activeContact.id || activeContact._id) ? { ...c, unreadCount: 0 } : c
                  )
                );
              })
              .catch(err => {
                logger.error("Failed to mark messages as read:", err);
              });
          }
        }
      })
      .catch(err => {
        logger.error("Failed to load messages:", err);
        setError("Failed to load messages");
      })
      .finally(() => setLoadingMessages(false));
  }, [activeContact]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleContactSelect = contact => {
    setActiveContact(contact);
    setShowChatArea(true);
  };

  const handleBackToList = () => {
    setShowChatArea(false);
    // Clear new conversation state if going back without creating
    if (!activeContact && newConversationUserIds.length > 0) {
      setNewConversationName("");
      setNewConversationUserIds([]);
      setSelectedUsersCache({});
    }
  };

  const handlePrepareConversation = async () => {
    logger.log("🔵 handlePrepareConversation called", { chatType, newConversationUserIds, newConversationName });

    // Validate based on chat type
    if (chatType === "single") {
      if (!newConversationUserIds.length) {
        setError("Please select a user to start a conversation");
        logger.log("❌ Validation failed: No user selected");
        return;
      }
      if (newConversationUserIds.length > 1) {
        setError("Single chat can only have one participant");
        logger.log("❌ Validation failed: Too many users selected");
        return;
      }
    } else if (chatType === "group") {
      if (!newConversationName.trim()) {
        setError("Please provide a group name");
        logger.log("❌ Validation failed: No group name");
        return;
      }
      if (newConversationUserIds.length < 1) {
        setError("Group chat must have at least 1 participant");
        logger.log("❌ Validation failed: Not enough participants");
        return;
      }
    }

    setError("");
    setLoadingConversations(true);

    try {
      // Generate conversation name based on chat type
      let conversationName =
        chatType === "single"
          ? generateSingleChatName(newConversationUserIds, selectedUsersCache)
          : newConversationName.trim() || "New Group Chat";

      // If this is a client conversation (selectedOrgId exists), append |client marker
      if (selectedOrgId) {
        conversationName = `${conversationName}|client`;
      }

      logger.log("🆕 Creating conversation immediately...", {
        chatType,
        newConversationUserIds,
        selectedUsersCache,
        newConversationName: conversationName,
      });

      // Validate initial message
      if (!initialMessage.trim()) {
        setError("Please enter a message to start the conversation");
        setLoadingConversations(false);
        return;
      }

      // If this is a client conversation (selectedOrgId exists), append |client marker
      let finalConversationName = conversationName;
      if (selectedOrgId) {
        finalConversationName = `${conversationName}|client`;
      }

      // Create conversation immediately with the user's initial message
      const conversationResponse = await messagingService.createConversation({
        name: finalConversationName,
        userIds: newConversationUserIds,
        message: {
          text: initialMessage.trim(),
        },
      });

      const newConversation = conversationResponse.data?.conversation || conversationResponse.data?.data;
      if (!newConversation) {
        throw new Error("Failed to create conversation");
      }

      logger.log("✅ Conversation created successfully", newConversation);

      // Add to contacts and set as active
      setContacts(prev => [newConversation, ...prev]);
      setActiveContact(newConversation);

      // Clear the creation form
      setNewConversationName("");
      setNewConversationUserIds([]);
      setInitialMessage("");
      setSelectedUsersCache({});
      setShowCreateModal(false);
      setShowChatArea(true);
      setMessages([]);

      // Load messages for the new conversation
      const conversationId = newConversation.id || newConversation._id;
      const res = await messagingService.getMessages(conversationId);
      const messagesData = res.data?.data || [];
      setMessages(messagesData);
    } catch (err) {
      logger.error("❌ Failed to create conversation:", err);
      showApiMessage(err, "create conversation");
      setError("Failed to create conversation. Please try again.");
    } finally {
      setLoadingConversations(false);
    }
  };

  // Helper to check if conversation is with a client (has |client marker)
  const isClientConversation = contact => {
    if (!contact) return false;
    const name = contact.name || "";
    return name.includes("|client");
  };

  // Helper to get display name without the |client marker
  const getDisplayName = contact => {
    if (!contact) return "";
    const name = contact.name || "";
    return name.replace(/\|client/g, "").trim();
  };

  // Check if we already have a chat with a given user
  const hasExistingChat = userId => {
    if (!userId) return false;
    return contacts.some(contact =>
      contact.participants?.some(p => {
        const pId = p.id || p._id || p.userId;
        return pId === userId;
      })
    );
  };

  // Helper to get name of a participant with full name priority
  const getParticipantName = p => {
    if (!p) return "";
    if (p.firstName && p.lastName) return `${p.firstName} ${p.lastName}`;
    return p.name || p.firstName || p.username || "Unknown";
  };

  const getFilteredContacts = () => {
    let filtered = contacts;

    // Filter by tab: Clients vs Staff
    if (activeTab === "Clients") {
      filtered = filtered.filter(contact => isClientConversation(contact));
    } else if (activeTab === "Staff" && showClientsTab) {
      filtered = filtered.filter(contact => !isClientConversation(contact));
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(contact => {
        const displayName = getDisplayName(contact);
        return displayName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    return filtered;
  };

  const filteredContacts = getFilteredContacts();

  // User Profile Dialog Component
  const UserProfileDialog = () => (
    <Dialog open={showUserProfile} onOpenChange={setShowUserProfile}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="text-center pb-4">
          <DialogTitle className="text-xl font-semibold text-gray-900">User Profile</DialogTitle>
        </DialogHeader>

        {selectedUser && (
          <div className="space-y-6">
            {/* Profile Header */}
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src={participantAvatarUrl(selectedUser, 96)}
                  alt={selectedUser.name || selectedUser.firstName || "User"}
                  className="object-cover w-24 h-24 rounded-full border-4 border-white shadow-lg"
                  onError={e => {
                    e.target.src = participantAvatarUrl(selectedUser, 96);
                  }}
                />
                <div
                  className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white ${
                    selectedUser.isBlocked ? "bg-red-500" : "bg-green-500"
                  }`}
                ></div>
              </div>

              <div className="mt-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedUser.name ||
                    `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() ||
                    "Unknown User"}
                </h3>
                {selectedUser.username && <p className="text-sm text-gray-600 mt-1">@{selectedUser.username}</p>}
                <div className="mt-2">
                  {selectedUser.isBlocked ? (
                    <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                      Blocked
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      Active
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Details */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-4">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Profile Information</h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selectedUser.firstName && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                      First Name
                    </label>
                    <p className="text-sm text-gray-900 mt-1 font-medium">{selectedUser.firstName}</p>
                  </div>
                )}

                {selectedUser.lastName && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Last Name</label>
                    <p className="text-sm text-gray-900 mt-1 font-medium">{selectedUser.lastName}</p>
                  </div>
                )}

                {selectedUser.username && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Username</label>
                    <p className="text-sm text-gray-900 mt-1 font-medium">@{selectedUser.username}</p>
                  </div>
                )}

                {selectedUser.emailAddress && (
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                    <p className="text-sm text-gray-900 mt-1 font-medium">{selectedUser.emailAddress}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={() => setShowUserProfile(false)}
            className="px-6 py-2 text-white rounded-lg transition-colors hover:opacity-90"
            style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const ChatListSidebar = () => (
    <div className={`${showChatArea ? "hidden md:flex" : "flex"} w-full md:w-96 bg-white flex-col h-full rounded-2xl`}>
      <div className="p-4 border-b border-gray-100 md:p-6">
        <div className="flex justify-between items-center mb-4 md:mb-6">
          <h1 className="text-xl font-semibold text-gray-900 md:text-2xl">Messages</h1>
          <button
            onClick={handleOpenCreateModal}
            className="flex justify-center items-center w-8 h-8 text-white rounded-full transition-colors md:w-10 md:h-10 hover:bg-opacity-90"
            style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <div className="relative mb-4 md:mb-6">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="py-2 pr-10 pl-4 w-full text-sm bg-gray-50 rounded-lg border-0 md:py-3 focus:outline-none focus:ring-2"
            style={{
              "--tw-ring-color": `${Constants.HOST_EXPERIENCE_BASE_COLOR}50`,
            }}
          />
          <Search className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
        </div>

        {currentTabFeatureDisabled && (
          <div className="mb-4 border border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm">{currentTabUpgradeCopy}</p>
            <HUpgradeButton size="sm" onClick={goToUpgrade} />
          </div>
        )}

        <div className="flex space-x-6 md:space-x-8">
          {availableTabs.map(tab => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`pb-3 text-sm font-medium relative transition-colors ${
                activeTab === tab ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
              style={activeTab === tab ? { color: Constants.HOST_EXPERIENCE_BASE_COLOR } : {}}
            >
              {tab}
              {activeTab === tab && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                  style={{
                    backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                  }}
                ></div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {loadingConversations ? (
          <div className="space-y-3 p-4 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center p-3">
                <div className="flex-shrink-0 w-12 h-12 bg-slate-200 rounded-full mr-3"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                  <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-full text-red-500">{error}</div>
        ) : filteredContacts.length === 0 ? (
          <div className="flex justify-center items-center h-full text-gray-500">No conversations found.</div>
        ) : (
          filteredContacts.map(contact => (
            <div
              key={contact.id}
              onClick={() => handleContactSelect(contact)}
              className={`flex items-center p-3 md:p-4 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 ${
                activeContact?.id === contact.id ? "bg-blue-50" : "border-transparent"
              }`}
              style={
                activeContact?.id === contact.id
                  ? {
                      borderLeftColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
                      backgroundColor: `${Constants.HOST_EXPERIENCE_BASE_COLOR}08`,
                    }
                  : {}
              }
            >
              {/* Conversation Avatar */}
              <div className="flex-shrink-0 mr-3 relative">
                <img
                  src={
                    contact.participants?.length === 2
                      ? participantAvatarUrl(getOtherParticipant(contact.participants))
                      : participantAvatarUrl(contact.participants?.[0])
                  }
                  alt={getDisplayName(contact) || "Conversation"}
                  className="object-cover w-12 h-12 rounded-full border-2 border-gray-200 shadow-sm"
                  onError={e => {
                    e.target.src = participantAvatarUrl(
                      contact.participants?.length === 2
                        ? getOtherParticipant(contact.participants)
                        : contact.participants?.[0]
                    );
                  }}
                />
                {/* Online indicator */}
                {contact.participants?.length === 2 &&
                  (() => {
                    const other = contact.participants.find(p => p.id !== getUser()?.id);
                    return other?.isOnline ? (
                      <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full"></span>
                    ) : null;
                  })()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                    {getDisplayName(contact) ||
                      (contact.participants?.length === 2
                        ? // For single chats, show both users' names
                          getSingleChatDisplayName(contact.participants)
                        : // For group chats, show the conversation name
                          `Chat with ${contact.participants?.map(getParticipantName).join(", ") || "Unknown"}`)}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className="flex-shrink-0 text-xs text-gray-400">
                      {formatMessageDate(contact.updatedOn || contact.lastMessage?.createdOn || contact.createdOn)}
                    </span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={e => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-40 p-1" align="end">
                        <div className="flex flex-col">
                          <button
                            onClick={e => handleDeleteConversation(contact.id, e)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Chat
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 truncate flex-1">
                    {contact.lastMessage?.content || contact.lastMessage?.text || "No messages yet"}
                  </p>
                  {contact.unreadCount > 0 && (
                    <span
                      className="ml-2 flex-shrink-0 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white rounded-full"
                      style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                    >
                      {contact.unreadCount > 99 ? "99+" : contact.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const Message = ({ message }) => {
    // Get current user to determine if message is own
    const currentUser = getUser();
    // Try different possible user ID fields for comparison
    const currentUserId = currentUser?.id || currentUser?.userId || currentUser?._id;
    const messageUserId = message.user?.id;
    const isOwn = messageUserId === currentUserId;

    // Check if message has media or attachments (API might return either)
    const mediaItems = message.media || message.attachments || [];
    const hasMedia = mediaItems.length > 0;

    if (hasMedia) {
      return (
        <div className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-4 md:mb-6`}>
          <div className={`flex flex-col ${isOwn ? "items-end" : "items-start"} max-w-xs lg:max-w-md`}>
            {/* Media content */}
            <div
              className={`grid gap-1 p-2 rounded-2xl md:gap-2 md:p-3 ${
                mediaItems.length === 1 ? "grid-cols-1" : "grid-cols-2"
              } ${isOwn ? "bg-blue-50" : "bg-gray-100"}`}
            >
              {mediaItems.map((media, index) => (
                <img
                  key={index}
                  src={media.url || media.resourceUrl}
                  alt={media.name || `Image ${index + 1}`}
                  className="object-cover w-full rounded-xl cursor-pointer hover:opacity-90"
                  style={{
                    maxHeight: mediaItems.length === 1 ? "300px" : "150px",
                    minHeight: "100px",
                  }}
                  onClick={() => window.open(media.url || media.resourceUrl, "_blank")}
                />
              ))}
            </div>

            {/* Message text if present */}
            {message.text && message.text.trim() && (
              <div
                className={`mt-2 px-4 py-3 rounded-2xl ${isOwn ? "text-white rounded-br-md" : "text-gray-900 bg-gray-200 rounded-bl-md"}`}
                style={isOwn ? { backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR } : {}}
              >
                <p className="text-sm leading-relaxed">{message.text}</p>
              </div>
            )}

            {/* Timestamp */}
            <div className="mt-1">
              <span className="text-xs text-gray-400">{formatMessageDate(message.createdOn)}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-4 md:mb-6`}>
        <div className={`flex flex-col ${isOwn ? "items-end" : "items-start"} max-w-xs lg:max-w-md`}>
          {/* Message bubble */}
          <div
            className={`px-4 py-3 rounded-2xl relative ${
              isOwn ? "text-white rounded-br-md" : "text-gray-900 bg-gray-200 rounded-bl-md"
            }`}
            style={isOwn ? { backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR } : {}}
          >
            <div className="flex items-center">
              <p className="flex-1 text-sm leading-relaxed">{message.text}</p>
              {isOwn && (
                <Popover
                  open={openMessageOptions === message.id}
                  onOpenChange={open => setOpenMessageOptions(open ? message.id : null)}
                >
                  <PopoverTrigger asChild>
                    <button className="ml-2 transition-colors text-white/70 hover:text-white" title="Message options">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-40 p-1" align="end">
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Timestamp */}
          <div className="mt-1">
            <span className="text-xs text-gray-400">{formatMessageDate(message.createdOn)}</span>
          </div>
        </div>
      </div>
    );
  };

  const ChatHeader = () => (
    <div className="flex justify-between items-center px-4 py-3 bg-white rounded-t-2xl border-b border-gray-100 md:px-6 md:py-4">
      <div className="flex items-center space-x-3">
        <button
          onClick={handleBackToList}
          className="p-2 -ml-2 text-gray-600 transition-colors md:hidden hover:text-gray-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Chat Avatar */}
        <div className="flex-shrink-0 relative">
          <img
            src={
              activeContact?.participants?.length === 2
                ? participantAvatarUrl(getOtherParticipant(activeContact.participants), 40)
                : activeContact?.participants?.[0]
                  ? participantAvatarUrl(activeContact.participants[0], 40)
                  : getInitialsAvatar(
                      newConversationUserIds.length > 0
                        ? selectedUsersCache[newConversationUserIds[0]]?.firstName +
                            " " +
                            selectedUsersCache[newConversationUserIds[0]]?.lastName ||
                            selectedUsersCache[newConversationUserIds[0]]?.name ||
                            "N"
                        : "C",
                      40
                    )
            }
            alt={getDisplayName(activeContact) || "Conversation"}
            className="object-cover w-10 h-10 rounded-full border-2 border-gray-200 shadow-sm"
            onError={e => {
              e.target.src = participantAvatarUrl(
                activeContact?.participants?.length === 2
                  ? getOtherParticipant(activeContact.participants)
                  : activeContact?.participants?.[0]
              );
            }}
          />
          {/* Online indicator */}
          {activeContact?.participants?.length === 2 &&
            (() => {
              const other = activeContact.participants.find(p => p.id !== getUser()?.id);
              return other?.isOnline ? (
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
              ) : null;
            })()}
        </div>

        <div>
          <h2 className="text-base font-semibold text-gray-900 md:text-lg">
            {activeContact
              ? getDisplayName(activeContact) ||
                (activeContact.participants?.length === 2 ? getSingleChatDisplayName(activeContact.participants) : "")
              : newConversationUserIds.length > 0
                ? chatType === "single"
                  ? generateSingleChatName(newConversationUserIds, selectedUsersCache)
                  : newConversationName || "New Group Chat"
                : "Chat"}
          </h2>
          <p className="text-xs text-gray-500 md:text-sm">
            {!activeContact && newConversationUserIds.length > 0
              ? "Type and send your first message to create this chat"
              : activeContact?.participants?.length === 2
                ? (() => {
                    const other = activeContact.participants.find(p => p.id !== getUser()?.id);
                    if (other?.isOnline) return "Online";
                    if (other?.lastSeen) return `Last seen ${formatMessageDate(other.lastSeen)}`;
                    return null;
                  })() || `${activeContact.participants.length} participants`
                : activeContact?.participants?.length > 2
                  ? `${activeContact.participants.length} participants`
                  : "Conversation"}
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-1 md:space-x-2">
        <button
          onClick={() => handleContactAction("call")}
          disabled={loadingUserDetails}
          className="flex justify-center items-center w-8 h-8 text-white rounded-full transition-colors md:w-10 md:h-10 hover:bg-opacity-90 disabled:opacity-50"
          style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
          title="Audio Call"
        >
          <Phone className="w-3 h-3 md:w-4 md:h-4" />
        </button>
        <button
          onClick={handleShowChatInfo}
          className="flex justify-center items-center w-8 h-8 text-white rounded-full transition-colors md:w-10 md:h-10 hover:bg-opacity-90"
          style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
          title="Chat Info"
        >
          <Info className="w-3 h-3 md:w-4 md:h-4" />
        </button>
      </div>
    </div>
  );

  const ChatArea = () => {
    if (!showChatArea && window.innerWidth < 768) {
      return null;
    }

    if (!activeContact && !showCreateModal && messages.length === 0 && newConversationUserIds.length === 0) {
      return (
        <div className="hidden md:flex flex-1 bg-white rounded-2xl border border-gray-200">
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a conversation</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Choose a conversation from the list to start messaging, or create a new conversation to get started.
            </p>
            <button
              onClick={handleOpenCreateModal}
              className="mt-6 px-4 py-2 text-white rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            >
              <Plus className="w-4 h-4 mr-2 inline" />
              New Conversation
            </button>
          </div>
        </div>
      );
    }

    const currentConversationIsClient = activeContact ? isClientConversation(activeContact) : selectedOrgId !== null;
    const conversationFeatureDisabled =
      (currentConversationIsClient && !canMessageCustomers) || (!currentConversationIsClient && !canMessageTeam);
    const conversationUpgradeCopy = currentConversationIsClient
      ? "Customer messaging is available on higher plans."
      : "Team messaging is available on higher plans.";

    return (
      <div
        className={`${showChatArea ? "flex" : "hidden md:flex"} flex-1 flex-col bg-white rounded-2xl overflow-hidden border-l border-gray-200`}
      >
        <ChatHeader />

        {conversationFeatureDisabled && (
          <div className="border-b border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm">{conversationUpgradeCopy}</p>
            <HUpgradeButton size="sm" onClick={goToUpgrade} />
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-3 py-3 bg-white md:px-6 md:py-6">
          {loadingMessages ? (
            <div className="space-y-4 animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-xs lg:max-w-md ${i % 2 === 0 ? "items-end" : "items-start"} flex flex-col`}>
                    <div className="h-16 bg-slate-200 rounded-2xl w-48"></div>
                    <div className="h-3 bg-slate-200 rounded w-16 mt-1"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full text-red-500">{error}</div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-full text-gray-500">
              {!activeContact && newConversationUserIds.length > 0
                ? "Type your first message below to start this chat"
                : "Start a conversation!"}
            </div>
          ) : (
            messages.map(message => <Message key={message.id} message={message} />)
          )}
          <div ref={messagesEndRef} />
        </div>

        <MessageInput
          onSendMessage={msg => {
            logger.log("🎯 MessageInput onSendMessage callback triggered", { msg });
            handleSendMessageWithCreation(msg);
          }}
          sendingMessage={sendingMessage}
          disabled={conversationFeatureDisabled || (!activeContact && newConversationUserIds.length === 0)}
        />
        {/* Debug info in development */}
        {process.env.NODE_ENV === "development" && (
          <div className="px-4 py-1 text-xs text-gray-400 bg-gray-50 border-t">
            Debug: activeContact={activeContact ? "yes" : "no"}, newConversationUserIds={newConversationUserIds.length},
            showChatArea={showChatArea ? "yes" : "no"}, chatType={chatType}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex p-2 h-screen md:p-8">
      <div className="flex gap-0 w-full bg-transparent rounded-2xl md:gap-4">
        <ChatListSidebar />
        <ChatArea />
      </div>

      <Dialog
        open={showCreateModal}
        onOpenChange={open => {
          logger.log("🔔 Dialog onOpenChange", {
            open,
            newConversationUserIds: newConversationUserIds.length,
            showChatArea,
            chatType,
          });
          if (!open) {
            // Only reset when closing via backdrop/ESC, not when preparing conversation
            // Check if we're preparing a conversation (newConversationUserIds has data OR chatArea is showing)
            // If we have selected users or chat area is showing, we're preparing a conversation, so don't reset
            if (newConversationUserIds.length === 0 && !showChatArea) {
              logger.log("🔄 Resetting modal state (no users selected, chat area not shown)");
              resetCreateModal();
            } else {
              logger.log("⏸️ Skipping reset (preparing conversation)", {
                hasUsers: newConversationUserIds.length > 0,
                showChatArea,
              });
            }
          }
          setShowCreateModal(open);
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Create New Chat</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Chat Type Selection */}
            <div>
              <label className="block mb-3 text-sm font-medium text-gray-700">Chat Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleChatTypeChange("single")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    chatType === "single"
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="font-medium">Single Chat</div>
                    <div className="text-xs text-gray-500 mt-1">Chat with one person</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleChatTypeChange("group")}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    chatType === "group"
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                    </div>
                    <div className="font-medium">Group Chat</div>
                    <div className="text-xs text-gray-500 mt-1">Add 2+ people</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Chat Name Input - Only for group chat */}
            {chatType === "group" && (
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Group Name</label>
                <Input
                  type="text"
                  value={newConversationName}
                  onChange={e => setNewConversationName(e.target.value)}
                  placeholder="Enter group name"
                  className="w-full"
                />
              </div>
            )}

            {/* Staff vs Client toggle */}
            <div>
              <label className="block mb-3 text-sm font-medium text-gray-700">
                {chatType === "single" ? "Search for contact" : "Add participants (minimum 2 required)"}
              </label>
              <div className="flex bg-gray-100 rounded-lg p-1 mb-3">
                {hasParentOrg && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchMode("parent_staff");
                      setSelectedOrgId(null);
                      setUserSearchTerm("");
                      setUserSearchResults([]);
                    }}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                      searchMode === "parent_staff"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Parent Staff
                  </button>
                )}
                {FeatureAccess.hasStaffTabAccess(currentUser) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchMode("staff");
                      setSelectedOrgId(null);
                      setUserSearchTerm("");
                      setUserSearchResults([]);
                    }}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                      searchMode === "staff" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Staff
                  </button>
                )}
                {FeatureAccess.hasClientsTabAccess(currentUser) && !isIndividualCustomer && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchMode("clients");
                      setUserSearchTerm("");
                      setUserSearchResults([]);
                      // Auto-select first client org if available
                      if (!selectedOrgId && clientOrgs.length > 0) {
                        setSelectedOrgId(clientOrgs[0].id.toString());
                      }
                    }}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                      searchMode === "clients"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Clients
                  </button>
                )}
              </div>

              {/* Client org picker (only when clients mode) */}
              {searchMode === "clients" && clientOrgs.length > 1 && (
                <div className="mb-3">
                  <Select
                    value={selectedOrgId || ""}
                    onValueChange={value => {
                      setSelectedOrgId(value || null);
                      setUserSearchTerm("");
                      setUserSearchResults([]);
                      setNewConversationUserIds([]);
                      setSelectedUsersCache({});
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={loadingOrgs ? "Loading organizations..." : "Select client organization"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {clientOrgs.map(org => (
                        <SelectItem key={org.id} value={org.id.toString()}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Input
                type="text"
                value={userSearchTerm}
                onChange={e => setUserSearchTerm(e.target.value)}
                placeholder={
                  searchMode === "clients"
                    ? "Search clients by name, email, or username"
                    : searchMode === "parent_staff"
                      ? "Search parent staff by name, email, or username"
                      : "Search staff by name, email, or username"
                }
                className="w-full"
              />
              {userSearchLoading && <div className="mt-1 text-xs text-gray-500">Searching...</div>}
              {userSearchError && <div className="mt-1 text-xs text-red-500">{userSearchError}</div>}
              {!userSearchLoading && filteredSearchResults.length > 0 && (
                <div className="mt-1 text-xs text-green-600">
                  Found {filteredSearchResults.length} user{filteredSearchResults.length !== 1 ? "s" : ""} in{" "}
                  {searchMode === "parent_staff"
                    ? "parent staff"
                    : searchMode === "staff"
                      ? "staff"
                      : selectedOrgId
                        ? clientOrgs.find(o => o.id.toString() === selectedOrgId)?.name || "clients"
                        : "clients"}
                </div>
              )}
              {filteredSearchResults.length > 0 && (
                <div className="overflow-y-auto relative z-10 mt-1 max-h-48 bg-white rounded-md border border-gray-200 shadow-lg">
                  {filteredSearchResults.map((user, index) => {
                    // Generate display name from available fields
                    const displayName =
                      user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.name || user.username || "Unknown User";

                    // Use id or fallback to index if needed
                    const userId = user.id || user._id || `user-${index}`;

                    return (
                      <div
                        key={userId}
                        className="flex items-center px-4 py-4 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                        onClick={() => {
                          logger.log("🎯 User selected:", user);

                          // Cache the user data before clearing search results
                          setSelectedUsersCache(prev => ({
                            ...prev,
                            [userId]: user,
                          }));

                          if (chatType === "single") {
                            // For single chat, replace the current selection
                            setNewConversationUserIds([userId]);
                          } else {
                            // For group chat, add to existing selection
                            if (!newConversationUserIds.includes(userId)) {
                              setNewConversationUserIds(prev => [...prev, userId]);
                            }
                          }
                          setUserSearchTerm("");
                          setUserSearchResults([]);
                        }}
                      >
                        <img
                          src={participantAvatarUrl(user)}
                          alt={displayName}
                          className="mr-3 w-12 h-12 rounded-full border-2 border-gray-200 shadow-sm object-cover"
                          onError={e => {
                            e.target.src = participantAvatarUrl(user);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-base font-semibold text-gray-900 truncate">{displayName}</span>
                            {hasExistingChat(userId) && (
                              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                Existing chat
                              </span>
                            )}
                            {user.username && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                @{user.username}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-700 truncate mt-1 font-medium">
                            {user.emailAddress || user.email || "No email"}
                          </div>
                          {/* Show selection status */}
                          {newConversationUserIds.includes(userId) && (
                            <div className="text-xs text-blue-600 mt-1 font-medium">✓ Selected</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {newConversationUserIds.length > 0 && (
                <div className="mt-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {chatType === "single"
                      ? "Selected Contact"
                      : `Selected Participants (${newConversationUserIds.length})`}
                  </div>
                  <div className="space-y-2">
                    {newConversationUserIds.map(userId => {
                      const user = selectedUsersCache[userId] ||
                        userSearchResults.find(u => u.id === userId) || {
                          id: userId,
                          firstName: "",
                          lastName: "",
                          username: "",
                          emailAddress: "",
                          imageUrl: "",
                          avatar: "",
                        };
                      const displayName =
                        user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username || user.name || `User ${userId}`;
                      return (
                        <div
                          key={userId}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center">
                            <img
                              src={participantAvatarUrl(user, 32)}
                              alt={displayName}
                              className="w-8 h-8 rounded-full mr-3 border border-gray-300"
                              onError={e => {
                                e.target.src = participantAvatarUrl(user, 32);
                              }}
                            />
                            <div>
                              <div className="font-medium text-gray-900">{displayName}</div>
                              <div className="text-sm text-gray-600">
                                {user.username && `@${user.username} • `}
                                <span className="font-medium">{user.emailAddress || user.email || "No email"}</span>
                              </div>
                            </div>
                          </div>
                          <button
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-100"
                            onClick={() => setNewConversationUserIds(prev => prev.filter(id => id !== userId))}
                            title="Remove"
                            type="button"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Initial Message Input */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Initial Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={initialMessage}
                onChange={e => setInitialMessage(e.target.value)}
                placeholder="Type your first message to start the conversation..."
                rows={3}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none"
                style={{
                  "--tw-ring-color": Constants.HOST_EXPERIENCE_BASE_COLOR,
                }}
              />
              <p className="mt-1 text-xs text-gray-500">This message will be sent when the conversation is created</p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm font-medium text-red-700">{error}</div>
              </div>
            </div>
          )}

          {/* Helper text for single chat */}
          {chatType === "single" && newConversationUserIds.length === 0 && !error && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-700">💡 Search and select a user above to start a single chat</div>
            </div>
          )}

          <div className="flex justify-end mt-6 space-x-3">
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setShowCreateModal(false);
                resetCreateModal();
              }}
              className="px-6 py-2 text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                logger.log("🔘 Continue button clicked", { chatType, newConversationUserIds, newConversationName });
                handlePrepareConversation();
              }}
              disabled={
                (chatType === "single" && newConversationUserIds.length !== 1) ||
                (chatType === "group" && (!newConversationName.trim() || newConversationUserIds.length < 1)) ||
                !initialMessage.trim() ||
                loadingConversations
              }
              title={(() => {
                const noMessage = !initialMessage.trim();
                const singleChatIssues = chatType === "single" && newConversationUserIds.length !== 1;
                const groupChatIssues =
                  chatType === "group" && (!newConversationName.trim() || newConversationUserIds.length < 2);

                if (noMessage) return "Please enter an initial message";
                if (singleChatIssues) return "Please select exactly one user to start a single chat";
                if (groupChatIssues) return "Please provide a group name and select at least 1 participant";
                if (loadingConversations) return "Creating conversation...";
                return "Continue";
              })()}
              className="px-6 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            >
              {loadingConversations ? "Creating..." : chatType === "single" ? "Start Chat" : "Create Group"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Info Dialog */}
      <Dialog open={showChatInfo} onOpenChange={setShowChatInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              {isGroupChat() ? "Group Info" : "Chat Info"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Chat Header Info */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                {isGroupChat() ? (
                  <Users className="w-10 h-10 text-gray-600" />
                ) : (
                  <User className="w-10 h-10 text-gray-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{getDisplayName(activeContact) || "Chat"}</h3>
              <p className="text-sm text-gray-500">
                {isGroupChat() ? `${chatMembers.length} members` : "Direct message"}
              </p>
            </div>

            {/* Members List */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {isGroupChat() ? "Group Members" : "Contact Details"}
              </h4>

              {loadingMembers ? (
                <div className="text-center py-4">
                  <div className="text-sm text-gray-500">Loading...</div>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {chatMembers.map((member, index) => (
                    <div key={member.id || index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <div className="relative mr-3">
                        <img
                          src={participantAvatarUrl(member)}
                          alt={member.name || member.username || "User"}
                          className="w-10 h-10 rounded-full border border-gray-300"
                          onError={e => {
                            e.target.src = participantAvatarUrl(member);
                          }}
                        />
                        {member.isOnline && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {member.firstName && member.lastName
                            ? `${member.firstName} ${member.lastName}`
                            : member.name || member.username || "Unknown User"}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {member.isOnline
                            ? "Online"
                            : member.lastSeen
                              ? `Last seen ${formatMessageDate(member.lastSeen)}`
                              : member.emailAddress || member.email || "No email"}
                        </div>
                        {member.username && <div className="text-xs text-gray-400 truncate">@{member.username}</div>}
                      </div>
                      {/* Show admin badge for group creator or current user */}
                      {(member.isAdmin || member.id === getUser()?.id) && isGroupChat() && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Admin</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Group Actions (only for group chats) */}
            {isGroupChat() && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Group Actions</h4>
                <div className="space-y-2">
                  <button className="w-full text-left p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <div className="font-medium">Leave Group</div>
                    <div className="text-sm text-red-500">Exit this group conversation</div>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowChatInfo(false)}
              className="px-6 py-2 text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <HReusableAlertModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setConversationToDelete(null);
        }}
        onConfirm={confirmDeleteConversation}
        title="Delete Conversation"
        description="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="primary"
      />

      <UserProfileDialog />

      {/* Contact Selection/Confirmation Dialog */}
      <Dialog open={showContactSelection} onOpenChange={setShowContactSelection}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Select Contact to Call</DialogTitle>
          </DialogHeader>

          {(() => {
            const currentUser = getUser();
            // Get unique users from messages, excluding current user
            const uniqueUsers = messages
              .map(msg => msg.user)
              .filter(
                (user, index, self) =>
                  user && user.id !== currentUser?.id && self.findIndex(u => u?.id === user.id) === index
              );

            if (uniqueUsers.length === 1 && selectedUser) {
              return (
                <div className="space-y-6">
                  <div className="text-center">
                    <img
                      src={participantAvatarUrl(selectedUser, 96)}
                      alt={selectedUser.name || selectedUser.firstName}
                      className="object-cover w-24 h-24 mx-auto rounded-full border-4 border-white shadow-lg"
                      onError={e => {
                        e.target.src = participantAvatarUrl(selectedUser, 96);
                      }}
                    />
                    <h3 className="mt-4 text-xl font-semibold text-gray-900">
                      {selectedUser.name || `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
                    </h3>
                    {selectedUser.username && <p className="text-sm text-gray-600 mt-1">@{selectedUser.username}</p>}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      {selectedUser.phoneNumber || selectedUser.phone
                        ? `Call ${selectedUser.firstName || "this user"} at ${selectedUser.phoneNumber || selectedUser.phone}?`
                        : `No phone number available for ${selectedUser.firstName || "this user"}`}
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowContactSelection(false);
                        setSelectedUser(null);
                      }}
                      className="px-6 py-2 text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => executeContactAction(selectedUser)}
                      disabled={!(selectedUser.phoneNumber || selectedUser.phone)}
                      className="px-6 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                    >
                      Call Now
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Select a participant to call:</p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {uniqueUsers.map(user => (
                    <button
                      key={user.id}
                      onClick={async () => {
                        setLoadingUserDetails(true);
                        try {
                          const response = await apiV2Service.getUserById(user.id);
                          const userData = response.data?.data || response.data;
                          executeContactAction(userData);
                        } catch (error) {
                          logger.error("Failed to fetch user details:", error);
                          toastUtils.error("Failed to load contact details");
                        } finally {
                          setLoadingUserDetails(false);
                        }
                      }}
                      disabled={loadingUserDetails}
                      className="w-full flex items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                    >
                      <img
                        src={
                          user.avatar ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.firstName || "U")}&background=random&size=48`
                        }
                        alt={user.name || user.firstName}
                        className="w-12 h-12 rounded-full mr-4 border-2 border-gray-200"
                        onError={e => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.firstName || "U")}&background=random&size=48`;
                        }}
                      />
                      <div className="flex-1 text-left">
                        <div className="font-medium text-gray-900">
                          {user.name || `${user.firstName || ""} ${user.lastName || ""}`.trim()}
                        </div>
                        {user.username && <div className="text-sm text-gray-600">@{user.username}</div>}
                      </div>
                      <Phone className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowContactSelection(false);
                      setSelectedUser(null);
                    }}
                    className="px-6 py-2 text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HostMessages;
